/**
 * API Client for Poshy Mail
 */

export const API_BASE = import.meta.env.VITE_API_URL || '';

interface ApiError extends Error {
  status: number;
}

async function handleResponse(response: Response) {
  if (!response.ok) {
    const error = new Error() as ApiError;

    try {
      const data = await response.json();
      error.message = data.error || `HTTP ${response.status}`;
    } catch {
      error.message = `HTTP ${response.status}`;
    }

    error.status = response.status;
    throw error;
  }

  // Handle empty responses
  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function getHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Add CSRF token if available
  const csrfToken = localStorage.getItem('csrfToken');
  if (csrfToken) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  return headers;
}

export const api = {
  async get(url: string) {
    const response = await fetch(`${API_BASE}${url}`, {
      method: 'GET',
      credentials: 'include',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  async post(url: string, data?: unknown, opts?: { keepalive?: boolean }) {
    const response = await fetch(`${API_BASE}${url}`, {
      method: 'POST',
      credentials: 'include',
      headers: getHeaders(),
      body: data ? JSON.stringify(data) : undefined,
      // keepalive lets the request finish after the page unloads (undo-send flush).
      keepalive: opts?.keepalive,
    });
    return handleResponse(response);
  },

  async put(url: string, data: unknown) {
    const response = await fetch(`${API_BASE}${url}`, {
      method: 'PUT',
      credentials: 'include',
      headers: getHeaders(),
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  async delete(url: string) {
    const response = await fetch(`${API_BASE}${url}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  async upload(url: string, file: File, onProgress?: (progress: number) => void) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.open('POST', `${API_BASE}${url}`);

      // Add CSRF token
      const csrfToken = localStorage.getItem('csrfToken');
      if (csrfToken) {
        xhr.setRequestHeader('X-CSRF-Token', csrfToken);
      }

      xhr.withCredentials = true;

      if (onProgress) {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            onProgress((e.loaded / e.total) * 100);
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            resolve(xhr.responseText);
          }
        } else {
          let msg = `Upload failed: ${xhr.status}`;
          try {
            const body = JSON.parse(xhr.responseText);
            if (body.error) msg = body.error;
          } catch { /* use default message */ }
          reject(new Error(msg));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed'));
      });

      const formData = new FormData();
      formData.append('file', file);
      xhr.send(formData);
    });
  },
};

// Typed API functions
export const emailApi = {
  list(folder: string, limit = 50, offset = 0) {
    return api.get(`/api/v1/emails?folder=${folder}&limit=${limit}&offset=${offset}`);
  },

  listByThread(threadId: string) {
    return api.get(`/api/v1/emails?threadId=${encodeURIComponent(threadId)}`);
  },

  get(id: string) {
    return api.get(`/api/v1/emails/${id}`);
  },

  send(data: {
    to: string[];
    cc?: string[];
    bcc?: string[];
    subject: string;
    body: string;
    attachments?: Array<{ filename: string; content: string; contentType: string }>;
    replyTo?: { messageId: string; references?: string };
  }) {
    return api.post('/api/v1/emails', data);
  },

  update(id: string, data: { isRead?: boolean; isStarred?: boolean; folderId?: string }) {
    return api.put(`/api/v1/emails/${id}`, data);
  },

  delete(id: string) {
    return api.delete(`/api/v1/emails/${id}`);
  },

  batch(emailIds: string[], action: string, folderId?: string) {
    return api.post('/api/v1/emails/batch', { emailIds, action, folderId });
  },

  search(query: string) {
    return api.get(`/api/v1/search?q=${encodeURIComponent(query)}`);
  },

  markAsSpam(id: string) {
    return api.post(`/api/v1/emails/${id}/spam`, { isSpam: true });
  },

  markAsNotSpam(id: string) {
    return api.post(`/api/v1/emails/${id}/spam`, { isSpam: false });
  },
};

export const attachmentApi = {
  upload(file: File, mailboxId: string, onProgress?: (progress: number) => void) {
    return api.upload(`/api/v1/attachments/upload?mailboxId=${encodeURIComponent(mailboxId)}`, file, onProgress);
  },

  delete(id: string) {
    return api.delete(`/api/v1/attachments/${id}`);
  },

  downloadUrl(id: string) {
    return `${API_BASE}/api/v1/attachments/${id}`;
  },
};

// The live drafts API takes recipients as address lists; the compose form holds strings.
type Recipient = { address: string; name?: string };
export function toRecipients(v: string | undefined): Recipient[] | undefined {
  if (v === undefined) return undefined;
  return v
    .split(/[,;]/)
    .map((e) => e.trim())
    .filter(Boolean)
    .map((e) => {
      const m = e.match(/^(.+?)\s*<(.+)>$/);
      return m ? { name: m[1].trim(), address: m[2].trim() } : { address: e };
    });
}
function draftBody(data: Partial<DraftInput>) {
  return { ...data, to: toRecipients(data.to), cc: toRecipients(data.cc), bcc: toRecipients(data.bcc) };
}

export interface DraftInput {
  mailboxId: string;
  to?: string;
  cc?: string;
  bcc?: string;
  subject?: string;
  textBody?: string;
  htmlBody?: string;
  attachmentIds?: string[];
  inReplyTo?: string;
  references?: string;
}

export const draftApi = {
  list() {
    return api.get('/api/v1/drafts');
  },

  get(id: string) {
    return api.get(`/api/v1/drafts/${id}`);
  },

  create(data: DraftInput) {
    return api.post('/api/v1/drafts', draftBody(data));
  },

  update(id: string, data: Partial<DraftInput>) {
    return api.put(`/api/v1/drafts/${id}`, draftBody(data));
  },

  delete(id: string) {
    return api.delete(`/api/v1/drafts/${id}`);
  },
};

export const folderApi = {
  list() {
    return api.get('/api/v1/folders');
  },

  create(name: string, mailboxId: string) {
    return api.post('/api/v1/folders', { name, mailboxId });
  },

  markAllRead(folderIdOrType: string) {
    return api.post(`/api/v1/folders/${encodeURIComponent(folderIdOrType)}/mark-all-read`);
  },

  update(id: string, name: string) {
    return api.put(`/api/v1/folders/${id}`, { name });
  },

  delete(id: string) {
    return api.delete(`/api/v1/folders/${id}`);
  },
};

export const contactApi = {
  list() {
    return api.get('/api/v1/contacts');
  },

  create(data: { email: string; name?: string; company?: string }) {
    return api.post('/api/v1/contacts', data);
  },

  update(id: string, data: { email?: string; name?: string; company?: string }) {
    return api.put(`/api/v1/contacts/${id}`, data);
  },

  delete(id: string) {
    return api.delete(`/api/v1/contacts/${id}`);
  },
};

// Admin API (requires admin role)
export interface AdminUser {
  id: string;
  email: string;
  displayName: string | null;
  role: 'admin' | 'user';
  createdAt: number;
  lastLogin: number | null;
  mailboxCount: number;
}

export interface AdminUserDetail {
  user: {
    id: string;
    email: string;
    displayName: string | null;
    role: 'admin' | 'user';
    createdAt: number;
    lastLogin: number | null;
  };
  mailboxes: Array<{
    id: string;
    user_id: string;
    address: string;
    display_name: string | null;
    is_primary: number;
    created_at: number;
  }>;
}

export interface CreateUserRequest {
  email: string;
  password: string;
  displayName?: string;
  role?: 'admin' | 'user';
  mailboxAddress?: string;
}

export interface UpdateUserRequest {
  displayName?: string;
  role?: 'admin' | 'user';
  password?: string;
}

export interface AddMailboxRequest {
  address: string;
  displayName?: string;
}

export const adminApi = {
  // Users
  listUsers(): Promise<{ users: AdminUser[] }> {
    return api.get('/api/v1/users');
  },

  getUser(id: string): Promise<AdminUserDetail> {
    return api.get(`/api/v1/users/${id}`);
  },

  createUser(data: CreateUserRequest) {
    return api.post('/api/v1/users', data);
  },

  updateUser(id: string, data: UpdateUserRequest) {
    return api.put(`/api/v1/users/${id}`, data);
  },

  deleteUser(id: string) {
    return api.delete(`/api/v1/users/${id}`);
  },

  // Mailboxes
  addMailbox(userId: string, data: AddMailboxRequest) {
    return api.post(`/api/v1/users/${userId}/mailboxes`, data);
  },
};
